// biome-ignore-all lint/suspicious/useIterableCallbackReturn: TODO - address later
// biome-ignore-all lint/correctness/noUnusedVariables: TODO - address later
import { type InfiniteData, useQueryClient } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { ActiveFiltersDisplay } from "@/components/active-filters-display";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import type { Account, AccountsResponse } from "@/hooks/api/use-accounts";
import { useFieldDefinitions } from "@/hooks/api/use-field-definitions";
import { useDRRReportState } from "@/hooks/use-drr-report-state";
import { useEntityReportData } from "@/hooks/use-entity-report-data";
import type { FilterColumn } from "@/lib/filter-display-utils";
import { convertToClientFilters } from "@/lib/filters";
import { getAccountColumns } from "@/pages/accounts/account-list/account-columns";
import { AccountsDataTable } from "@/pages/accounts/account-list/accounts-data-table";
import type { CustomFieldDefinition } from "@/types/custom-fields";
import type { DeepResearchReportMetadata } from "@/types/deep-research-reports";
import type { UnifiedTableColumn } from "@/types/table-column";
import { AIQueryColumnModal } from "./ai-query-column-modal";
import {
  ensureReportColumnsSelected,
  toFilterColumns,
  toStringParams,
  useDrrAIFieldsSync,
  useDrrRequiredColumns,
} from "./drr-table-common";

interface DRRAccountsTableProps {
  report: DeepResearchReportMetadata;
  mode: "editor" | "viewer";
  onRowSelect?: (row: Record<string, unknown>) => void;
  filters?: Record<string, unknown>; // For draft mode filters from parent
  onFiltersChange?: (filters: Record<string, unknown>) => void; // For draft mode filter updates
  onRecordCountChange?: (count: number) => void;
}

/**
 * Wrapper component that uses the main AccountsDataTable for DRR.
 * This provides a cleaner integration by reusing all the existing table features.
 */
export function DRRAccountsTable({
  report,
  mode,
  onRowSelect,
  filters: parentFilters,
  onFiltersChange,
  onRecordCountChange,
}: DRRAccountsTableProps) {
  const queryClient = useQueryClient();
  const [showAIModal, setShowAIModal] = useState(false);
  // Get the report's original filters for display
  const reportFilters = useMemo(() => {
    // Convert server format to client format
    const filters = report.row_filters;
    // Ensure it's a plain object, not a FilterGroup
    if (filters && typeof filters === "object" && !Array.isArray(filters)) {
      // Check if it's a FilterGroup (has operator and filters properties)
      if ("operator" in filters && "filters" in filters) {
        // Use the existing convertToClientFilters function
        return convertToClientFilters(filters);
      }
      return filters as Record<string, unknown>;
    }
    return {};
  }, [report.row_filters]);

  // Get field definitions for filter columns
  const { data: fieldDefs } = useFieldDefinitions("accounts");

  // Build base account columns (core only)
  const accountColumns = useMemo(() => {
    const filterOptions = fieldDefs
      ? {
          type:
            fieldDefs.find((f) => f.id === "type" && f.has_enum_values)
              ?.enum_values || [],
          record_type_name:
            fieldDefs.find(
              (f) => f.id === "record_type_name" && f.has_enum_values,
            )?.enum_values || [],
        }
      : undefined;

    return getAccountColumns(
      filterOptions,
      [], // customFields (appended separately via selected custom fields)
      [], // connections
      false, // isLoadingConnections
    );
  }, [fieldDefs]);

  // Use isolated DRR state for this report with columns
  const drrState = useDRRReportState(report, "account", accountColumns);

  // Derive selected custom fields (includes AI fields selected in the column selector)
  const selectedCustomFields = useMemo<CustomFieldDefinition[]>(() => {
    const selectable =
      drrState.stateOverride.columns.selectedCustomColumns || [];
    const defs = selectable
      .map((c: { customField?: CustomFieldDefinition }) => c.customField)
      .filter(Boolean) as CustomFieldDefinition[];
    // Deduplicate by id
    const map = new Map<string, CustomFieldDefinition>();
    defs.forEach((d) => map.set(d.id, d));
    return Array.from(map.values());
  }, [drrState.stateOverride.columns.selectedCustomColumns]);

  // Persist AI field selection to report is handled by useDrrAIFieldsSync below

  // Build full columns including selected custom/AI fields for filter/sort metadata
  const fullAccountColumns = useMemo<UnifiedTableColumn<Account>[]>(() => {
    const filterOptions = fieldDefs
      ? {
          type:
            fieldDefs.find((f) => f.id === "type" && f.has_enum_values)
              ?.enum_values || [],
          record_type_name:
            fieldDefs.find(
              (f) => f.id === "record_type_name" && f.has_enum_values,
            )?.enum_values || [],
        }
      : undefined;

    return getAccountColumns(filterOptions, selectedCustomFields, [], false);
  }, [fieldDefs, selectedCustomFields]);

  // In editor mode, use parent filters but still use DRR column state
  const filters = mode === "editor" ? parentFilters || {} : drrState.filters;

  // Handle filter changes based on mode
  const handleFiltersChange = useMemo(() => {
    if (mode === "editor") {
      return onFiltersChange;
    }
    return (newFilters: Record<string, unknown>) => {
      drrState.updateFilters(newFilters);
    };
  }, [mode, onFiltersChange, drrState]);

  // Get query params for the data fetching - convert to string format
  const queryParams = useMemo(() => {
    // In editor mode, use parentFilters; in viewer mode, use report's saved filters
    const params =
      mode === "editor" && parentFilters
        ? drrState.getQueryParamsWithFilters(parentFilters, {
            columns: fullAccountColumns,
            searchTerm: "",
            limit: 200,
          })
        : drrState.getQueryParams({
            columns: fullAccountColumns,
            searchTerm: "",
            limit: 200,
          });

    return toStringParams(params as Record<string, unknown>);
  }, [mode, parentFilters, drrState, fullAccountColumns]);

  // Build filter columns from account columns
  const filterColumns = useMemo(() => {
    return toFilterColumns(fullAccountColumns) as unknown as FilterColumn[];
  }, [fullAccountColumns]);

  // Compute required column ids from filters
  const { computeSelectionReady } = useDrrRequiredColumns(
    reportFilters as Record<string, unknown>,
    filters as Record<string, unknown>,
  );

  // Ensure only filtered columns are auto-selected (AI handled on mount/reset in hook)
  useEffect(() => {
    const colState = drrState.stateOverride.columns;
    ensureReportColumnsSelected(
      report,
      reportFilters as Record<string, unknown>,
      filters as Record<string, unknown>,
      colState,
      { includeAI: false },
    );
  }, [
    report.id,
    drrState.stateOverride.columns,
    report,
    filters,
    reportFilters,
  ]);

  // Gate DRR fetch until required columns are selected (prevents first-load gap)
  const selectionReady = useMemo(() => {
    const colState = drrState.stateOverride.columns;
    return computeSelectionReady(colState.isColumnSelected);
  }, [drrState.stateOverride.columns, computeSelectionReady]);

  // Use our adapter hook to get the right data source
  const dataQuery = useEntityReportData({
    entityType: "account",
    mode,
    report,
    queryParams,
    enabled: selectionReady,
  }) as {
    data: InfiniteData<AccountsResponse> | undefined;
    fetchNextPage: () => void;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    isLoading: boolean;
    isError: boolean;
    error: unknown;
    exportAllData?: () => Promise<Record<string, unknown>[]>;
  };

  // Notify parent of current record count for submit gating
  useEffect(() => {
    if (!onRecordCountChange) return;
    type PageLike = { results: unknown[]; total?: number };
    const pages = (dataQuery.data?.pages as unknown as PageLike[]) || [];
    if (pages.length === 0) {
      onRecordCountChange(0);
      return;
    }
    const lastPage = pages[pages.length - 1];
    const total =
      typeof lastPage.total === "number"
        ? lastPage.total
        : lastPage.results.length;
    onRecordCountChange(total);
  }, [dataQuery.data, onRecordCountChange]);

  // Create state override for editor mode (uses parent filters but DRR column state)
  const editorStateOverride = useMemo(() => {
    if (mode !== "editor") return undefined;

    // Local ref to prevent stale merges across rapid successive updates
    const filtersRef = { current: parentFilters || {} } as {
      current: Record<string, unknown>;
    };

    return {
      columns: drrState.stateOverride.columns,
      filters: {
        activeFilters: parentFilters || {},
        updateFilter: (key: string, value: unknown) => {
          if (onFiltersChange && parentFilters) {
            const next = { ...filtersRef.current, [key]: value };
            filtersRef.current = next;
            onFiltersChange(next);
            // Invalidate live accounts queries so sort changes take effect immediately
            if (key === "sortColumn" || key === "sortDirection") {
              void queryClient.invalidateQueries({
                queryKey: ["datahub", "accounts"],
                exact: false,
              });
              void queryClient.invalidateQueries({
                queryKey: ["datahub/accounts/infinite"],
                exact: false,
              });
            }
          }
        },
        clearFilter: (key: string) => {
          if (onFiltersChange && parentFilters) {
            const newFilters = { ...filtersRef.current } as Record<
              string,
              unknown
            >;
            delete newFilters[key];
            filtersRef.current = newFilters;
            onFiltersChange(newFilters);
          }
        },
        clearAllFilters: () => {
          if (onFiltersChange) {
            filtersRef.current = {};
            onFiltersChange({});
          }
        },
      },
      getQueryParams: (options: {
        searchTerm?: string;
        columns?: unknown[];
        limit?: number;
      }) => {
        // In editor mode, use parentFilters for building query params
        return drrState.getQueryParamsWithFilters(parentFilters || {}, options);
      },
    };
  }, [mode, drrState, parentFilters, onFiltersChange, queryClient]);

  // Keep a ref synced with latest parentFilters
  const latestFiltersRef = useRef<Record<string, unknown>>(parentFilters || {});
  useEffect(() => {
    latestFiltersRef.current = parentFilters || {};
  }, [parentFilters]);

  // Persist AI field selection (deduped) in editor mode
  useDrrAIFieldsSync(mode, report, selectedCustomFields);

  // Use the actual AccountsDataTable with our custom data source
  return (
    <>
      {mode === "viewer" && (
        <Card className="p-4 mb-4 flex-shrink-0">
          <div className="space-y-2">
            <div className="text-sm font-medium">
              Report Filters (Read-only)
            </div>
            <ActiveFiltersDisplay
              filters={reportFilters}
              columns={filterColumns}
              clearFilter={() => {}} // Read-only
              clearAllFilters={() => {}}
              readOnly={true}
            />
          </div>
        </Card>
      )}

      <div className="relative flex-grow min-h-0">
        <AccountsDataTable
          dataSource={dataQuery}
          onRowClick={
            onRowSelect
              ? (account) =>
                  onRowSelect({
                    entity_id: account.id,
                    data: account,
                  })
              : undefined
          }
          stateOverride={
            mode === "viewer" ? drrState.stateOverride : editorStateOverride
          }
          onFiltersChange={handleFiltersChange}
          onExportCSV={dataQuery.exportAllData}
          rightToolbarActions={
            mode === "editor" && report.status === "draft" ? (
              <Button
                size="sm"
                onClick={() => setShowAIModal(true)}
                data-testid="new-ai-field-button"
              >
                <Sparkles className="h-4 w-4 mr-1" /> New AI Field
              </Button>
            ) : undefined
          }
        />
      </div>

      {mode === "editor" && report.status === "draft" && (
        <AIQueryColumnModal
          open={showAIModal}
          onClose={() => setShowAIModal(false)}
          onSave={(partial) => {
            // Dispatch a custom event to be handled by upper-level logic
            const event = new CustomEvent("drr-create-ai-field", {
              detail: {
                entityType: "account",
                reportId: report.id,
                field: partial,
              },
            });
            window.dispatchEvent(event);
          }}
        />
      )}
    </>
  );
}
